import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CalenderIcon } from '../../assets/icons/calender-v2-icon.svg';
import { ReactComponent as CardPosIcon } from '../../assets/icons/card-pos-icon.svg';
import { ReactComponent as FleshIcon } from '../../assets/icons/flash-icon.svg';
import { ReactComponent as PeopleIcon } from '../../assets/icons/people-icon.svg';
import { useAppBranding } from '../../config/branding';
import { chartTimePeriod } from '../../config/enum';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setEndConsumerData, setTimePeriod } from '../../redux/slices/EndConsumerSlice';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../redux/slices/PageTitleSlice';
import CustomDatePicker from '../../shared-components/custom-date-range/CustomDatePicker';
import CustomRadioButton from '../../shared-components/custom-radio-button/CustomRadioButton';
import CustomToggleButton from '../../shared-components/custom-toggle-button/CustomToggleButton';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import endConsumerApi from '../../utils/api/end-consumer-v2';

import AfterDatasetsDrawChart from './components/ChartComponents/AfterDatasetsDrawChart';
import TotalPowerConsumedChart from './components/ChartComponents/TotalPowerConsumedChart';
import TotalRevenueChart from './components/ChartComponents/TotalRevenueChart';
import EndConsumersList from './components/EndConsumersList';

/**
 * EndConsumerManagementV2
 * @returns The rendered EndConsumerManagementV2 component
 */
function EndConsumerManagementV2() {
  const branding = useAppBranding();
  const { t, i18n } = useTranslation('endConsumer');
  const dispatch = useAppDispatch();
  const endConsumer = useAppSelector((state) => state.endConsumerType);
  const [revenueData, setRevenueData] = useState<any>({
    totalActiveConsumers: 0,
    totalPowerConsumedInKwh: 0,
    personalSessionCount: 0,
    freeSessionCount: 0,
    paidSessionCount: 0,
  });

  /**
   * Fetching card list api
   */
  const handleCardListApi = async () => {
    try {
      const response = await endConsumerApi.fetchTotalConsumerCard({
        type: endConsumer.endConsumerType,
        timePeriod: endConsumer.reportFilter.range,
        // startDate: endConsumer.reportFilter.startDate,
        // endDate: endConsumer.reportFilter.endDate,
      });

      const {
        totalActiveConsumers,
        totalPowerConsumedInKwh,
        personalSessionCount,
        freeSessionCount,
        paidSessionCount,
      } = response?.data.cardDetails || {};
      setRevenueData({
        totalActiveConsumers: totalActiveConsumers || 0,
        totalPowerConsumedInKwh: totalPowerConsumedInKwh || 0,
        personalSessionCount: personalSessionCount || 0,
        freeSessionCount: freeSessionCount || 0,
        paidSessionCount: paidSessionCount || 0,
      });
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: error?.response?.status || 500,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Handle the change graph type
   * @param index - The selected index
   * @param range - The time range string
   */
  const changeGraphType = (
    index: number,
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    let startDate = dayjs().subtract(24, 'hour').format('YYYY-MM-DD');
    const endDate = dayjs().format('YYYY-MM-DD');

    switch (index) {
      case 0:
        startDate = dayjs().subtract(24, 'hour').format('YYYY-MM-DD');
        break;
      case 1:
        startDate = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
        break;
      case 2:
        startDate = dayjs().subtract(1, 'month').format('YYYY-MM-DD');
        break;
      case 3:
        startDate = dayjs().subtract(1, 'year').format('YYYY-MM-DD');
        break;

      default:
        break;
    }
    dispatch(setTimePeriod({ index, startDate, endDate, range }));
  };

  /**
   * Handle input change
   * @param name - The field name
   * @param value - The field value
   */
  const handleInputChange = (name: string, value: string | number) => {
    dispatch(setEndConsumerData({ name, value: String(value) }));
  };

  useEffect(() => {
    handleCardListApi();
  }, [endConsumer]);

  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('endConsumer'),
      }),
    );
  }, [t]);

  return (
    <div className="end-consumer-management-v2" dir={i18n.dir()}>
      <div className="end-consumer-management-v2-top">
        <div className="header-controls">
          <div className="toggle-button-content">
            <CustomToggleButton
              key={endConsumer.activePeriodIndex}
              labels={chartTimePeriod.map((duration) => t(duration.label))}
              onToggle={(index) => changeGraphType(index, chartTimePeriod[index].value)}
              defaultActiveIndex={endConsumer.activePeriodIndex}
            />
          </div>
          <div className="radio-button-content">
            {endConsumer?.selectedEndConsumer?.userCustomId === 'love' && (
              <>
                <CustomDatePicker
                  inputLabel={t('startDate')}
                  value={endConsumer.reportFilter.startDate}
                  name="reportFilter.startDate"
                  valueRequired
                  onChangeValue={handleInputChange}
                  placeholder={t('dd/mm/yyyy')}
                />

                <CustomDatePicker
                  inputLabel={t('endDate')}
                  value={endConsumer.reportFilter.endDate}
                  name="reportFilter.endDate"
                  onChangeValue={handleInputChange}
                  valueRequired
                  placeholder={t('dd/mm/yyyy')}
                />
              </>
            )}
            <CustomRadioButton
              name="chargerType"
              value="commercial"
              label={t('commercial')}
              selected={endConsumer.endConsumerType === 'commercial'}
              onChangeFunc={() => handleInputChange('endConsumerType', 'commercial')}
            />

            <CustomRadioButton
              name="chargerType"
              value="personal"
              label={t('personal')}
              selected={endConsumer.endConsumerType === 'personal'}
              onChangeFunc={() => handleInputChange('endConsumerType', 'personal')}
            />
          </div>
        </div>

        <div className="revenue-content">
          <div className="revenue-table">
            <p className="title">{t('totalActiveConsumers')}</p>
            <div className="value-content">
              <p className="value">{revenueData.totalActiveConsumers}</p>
              <PeopleIcon className="icon" />
            </div>
          </div>

          <div className="revenue-table">
            <p className="title">{t('totalPowerConsumed')}</p>
            <div className="value-content">
              <p className="value">{ToFixedValue(revenueData.totalPowerConsumedInKwh)}</p>
              <FleshIcon className="icon" />
            </div>
          </div>
          {endConsumer.endConsumerType !== 'personal' && (
            <>
              <div className="revenue-table">
                <p className="title">{t('paidSessions')}</p>
                <div className="value-content">
                  <p className="value">{revenueData.paidSessionCount}</p>
                  <CardPosIcon className="icon" />
                </div>
              </div>
              <div className="revenue-table">
                <p className="title">{t('freeSessions')}</p>
                <div className="value-content">
                  <p className="value">{revenueData.freeSessionCount}</p>
                  <p className="icon">{branding.text.currency}</p>
                </div>
              </div>
            </>
          )}
          {endConsumer.endConsumerType === 'personal' && (
            <div className="revenue-table">
              <p className="title">{t('totalSessions')}</p>
              <div className="value-content">
                <p className="value">{revenueData.personalSessionCount}</p>
                <CalenderIcon className="icon" />
              </div>
            </div>
          )}
        </div>
        <div className="chart-content">
          <TotalRevenueChart />
          <TotalPowerConsumedChart />
          <AfterDatasetsDrawChart />
        </div>
      </div>
      <div className="end-consumer-management-v2-bottom">
        <EndConsumersList />
      </div>
    </div>
  );
}

export default EndConsumerManagementV2;
